import {
  Component, input, output, ContentChild, TemplateRef,
  signal, computed, viewChild, AfterViewInit, effect,
  HostListener
} from '@angular/core';
import { TableLazyLoadEvent, TableRowExpandEvent, TableRowCollapseEvent } from 'primeng/table';
import { toDisplayLabel } from '../../utils/label.util';

export interface TableColumn {
  field: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  type?: 'text' | 'number' | 'date' | 'status' | 'percent';
  statusField?: string;
  /** For type 'percent': renders a mini progress bar under/next to the value */
  showBar?: boolean;
  /** Renders a colored dot before the cell text using the given row field as CSS color */
  dotField?: string;
  /** For type 'percent' + showBar: row field holding the 0..1 fill ratio (defaults to field) */
  barField?: string;
  /** For type 'percent' + showBar: row field holding the tier key ('good' | 'watch' | 'low') tinting the bar */
  tierField?: string;
  /**
   * Rowspan support: given the full dataset and current row index returns
   * 1 for a normal cell, >1 to span that many rows, or 0 when the cell is
   * covered by a previous row's span (no td rendered).
   */
  rowspanOf?: (data: any[], index: number) => number | undefined;
  class?: string;
  width?: number;
  style?: Record<string, string>;
  isHidden?: boolean;
  /** Pins the column to the left ('start') or right ('end') edge while the table scrolls horizontally. */
  sticky?: 'start' | 'end';
}

export interface TableAction {
  label: string;
  icon: string;
  tooltip?: string;
  disabled?: boolean;
  severity?: string;
  condition?: (row: any) => boolean;
}

export interface PaginationState {
  first: number;
  rows: number;
  totalRecords: number;
  pageCount: number;
  currentPage: number;
}

@Component({
  selector: 'app-primeng-table',
  templateUrl: './primeng-table.component.html',
  styleUrls: ['./primeng-table.component.scss'],
  standalone: false,
})
export class PrimengTableComponent implements AfterViewInit {
  private static nextId = 0;
  // ── Inputs (signal-based) ──
   data = input<any[]>([]);
   columns = input<TableColumn[]>([]);
   title = input<string>('');
   subtitle = input<string>('');
  tableHeightEvent = output<number>();

   /** Safe text getters so a non-string title/subtitle can never render "[object Object]". */
   get titleText(): string { return toDisplayLabel(this.title()); }
   get subtitleText(): string { return toDisplayLabel(this.subtitle()); }

   /** Safe column header text. */
   columnHeaderText(col: TableColumn): string { return toDisplayLabel(col?.header); }

   paginator = input<boolean>(false);
   rows = input<number>(10);
   rowsPerPageOptions = input<number[]>([10, 25, 50]);
   sortable = input<boolean>(false);
   filterable = input<boolean>(false);
   exportable = input<boolean>(false);
   showSearchButton = input<boolean>(false);
   showFiltersToggle = input<boolean>(false);
   showAddButton = input<boolean>(false);
   addButtonLabel = input<string>('Add Entry');
   addButtonIcon = input<string>('pi pi-plus');
   selectionMode = input<'single' | 'multiple' | undefined>(undefined);
   showCheckbox = input<boolean>(true);
   dataKey = input<string>('id');
   expandable = input<boolean>(false);
   defaultExpandAll = input<boolean>(true);
   lazy = input<boolean>(false);
   loading = input<boolean>(false);
   totalRecords = input<number>(0);
   globalFilterFields = input<string[]>([]);
  scrollable = input<boolean>(false);
  scrollHeight = input<string>('400px');
  /**
   * Pins the row-number column to the left and any column flagged
   * `sticky: 'end'` (e.g. Actions) to the right while the table scrolls sideways.
   */
  stickyColumns = input<boolean>(false);
   emptyMessage = input<string>('No records found');
   striped = input<boolean>(true);
   hoverHighlight = input<boolean>(true);
   compact = input<boolean>(false);
   showHeader = input<boolean>(true);
   showFooter = input<boolean>(false);
   currentPageReportTemplate = input<string>('');
   rowStyleClassFn = input<((row: any) => string) | null>(null);

   selection = input<any | any[]>([]);
   sortField = input<string>('');
   sortOrder = input<number>(1);

  @ContentChild('rowexpansion', { static: false }) rowExpansionTemplate?: TemplateRef<any>;
  @ContentChild('expandedrow', { static: false }) expandedRowTemplate?: TemplateRef<any>;
  @ContentChild('emptytemplate', { static: false }) emptyTemplate?: TemplateRef<any>;
  @ContentChild('captiontemplate', { static: false }) captionTemplate?: TemplateRef<any>;
  @ContentChild('bodytemplate', { static: false }) bodyTemplate?: TemplateRef<any>;
  @ContentChild('headertemplate', { static: false }) headerTemplate?: TemplateRef<any>;
  @ContentChild('celltemplate', { static: false }) cellTemplate?: TemplateRef<any>;

  // ── Outputs (signal-based) ──
  readonly selectionChange = output<any | any[]>();
  readonly rowSelect = output<any>();
  readonly rowUnselect = output<any>();
  readonly rowClick = output<any>();
  readonly lazyLoad = output<TableLazyLoadEvent>();
  readonly pageChange = output<PaginationState>();
  readonly sortChange = output<any>();
  readonly rowExpand = output<TableRowExpandEvent>();
  readonly rowCollapse = output<TableRowCollapseEvent>();
  readonly filterChange = output<string>();
  readonly clearFiltersEvent = output<void>();
  readonly addEntry = output<void>();

  // ── Internal signals ──
  readonly globalFilter = signal('');
  readonly dt = viewChild<any>('dt');
  readonly first = signal(0);
  readonly expandedRows = signal<Record<string, boolean>>({});
  readonly filtersVisible = signal(false);
  /** Unique per instance so multiple tables on one page never share a DOM id. */
  readonly searchBoxId = `p-table-search-box-${PrimengTableComponent.nextId++}`;
  readonly columnFilters = signal<Record<string, string>>({});
  /** Dynamically computed table scroll height so the page does not scroll. */
  // readonly dynamicScrollHeight = signal<string>(this.scrollHeight());

  // ── Computed ──
  readonly visibleColumns = computed(() =>
    this.columns().filter(c => !c.isHidden)
  );

  readonly hasCheckbox = computed(() => this.showCheckbox() && this.selectionMode() === 'multiple');
  readonly hasRadio = computed(() => this.selectionMode() === 'single');
  readonly hasExpand = computed(() => this.expandable());

  readonly paginationInfo = computed(() => {
    const total = this.lazy() ? this.totalRecords() : this.data().length;
    const f = this.first();
    const r = this.rows();
    const start = total > 0 ? f + 1 : 0;
    const end = Math.min(f + r, total);
    return { start, end, total };
  });

  readonly styleClass = computed(() => {
    const classes = ['p-datatable-sm'];
    if (this.striped()) classes.push('p-datatable-striped');
    if (this.hoverHighlight()) classes.push('p-datatable-hoverable');
    return classes.join(' ');
  });

  /** Sticky positioning classes for a column, empty when the feature is off. */
  stickyClasses(col: TableColumn): string {
    if (!this.stickyColumns() || !col.sticky) return '';
    return col.sticky === 'start'
      ? 'p-table-cell-sticky p-table-cell-sticky--start'
      : 'p-table-cell-sticky p-table-cell-sticky--end';
  }

  private readonly resetFirstOnData = effect(() => {
    this.data();
    if (!this.lazy()) {
      this.first.set(0);
    }
  });

  private readonly autoExpandAllOnData = effect(() => {
    if (this.expandable() && this.defaultExpandAll()) {
      const dataList = this.data();
      const key = this.dataKey();
      if (key && dataList && dataList.length > 0) {
        const keys: Record<string, boolean> = {};
        for (const row of dataList) {
          if (row && row[key] != null) {
            keys[String(row[key])] = true;
          }
        }
        this.expandedRows.set(keys);
      }
    }
  });

  /** When scrollable flips to true at runtime, recompute the fill-height (ngAfterViewInit only runs once). */
  // private readonly recomputeScrollOnFlip = effect(() => {
  //   if (this.scrollable()) {
  //     this.recomputeScrollHeight();
  //   }
  // });

  constructor() { 
    this.getTableHeightByPage();
  }

  // ngAfterViewInit(): void {

    // if (this.scrollable()) {
    //   setTimeout(() => this.recomputeScrollHeight(), 0);
    // }
  // }

  // @HostListener('window:resize')
  // onWindowResize(): void {
  //   if (this.scrollable()) {
  //     this.recomputeScrollHeight();
  //   }
  // }

  // private recomputeScrollHeight(): void {
  //   const h = this.getTableHeight();
  //   if (h) {
  //     // this.dynamicScrollHeight.set(h);
  //   }
  // }

  /**
   * Computes a scroll height that makes the table fill the available viewport
   * so the page itself does not scroll. Returns 'flex' when not scrollable.
   */
  getTableHeight() {
    if (this.scrollable()) {
      const paginationHeight =
        document.getElementsByClassName('p-paginator').item(0)?.clientHeight || 0;
      const tableElement = document
        ?.getElementsByClassName('p-datatable-table-container')
        .item(0)
        ?.getBoundingClientRect();
      if (tableElement) {
        return (
          window.innerHeight -
          (Math.floor(tableElement.y) + paginationHeight + 25)) + 'px';
      }
      return null;
    }
    return 'flex';
  }

  // ── Methods ──
  onGlobalFilter(value: string): void {
    this.globalFilter.set(value);
    const table = this.dt();
    if (table) {
      if (!this.lazy()) {
        this.first.set(0);
      }
      table.filterGlobal(value, 'contains');
    }
  }

  onPageChange(event: any): void {
    this.first.set(event.first ?? 0);
    this.pageChange.emit({
      first: event.first ?? 0,
      rows: event.rows ?? this.rows(),
      totalRecords: this.lazy() ? this.totalRecords() : this.data().length,
      pageCount: event.pageCount ?? 1,
      currentPage: event.page ?? 0,
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.lazyLoad.emit(event);
  }

  onSortChange(event: any): void {
    this.sortChange.emit(event);
  }

  onRowSelect(event: any): void {
    this.rowSelect.emit(event.data ?? event);
  }

  onRowClick(data: any): void {
    this.rowClick.emit(data);
  }

  onRowUnselect(event: any): void {
    this.rowUnselect.emit(event.data ?? event);
  }

  onSelectionChange(event: any): void {
    this.selectionChange.emit(event);
  }

  onRowExpand(event: TableRowExpandEvent): void {
    this.expandedRows.update(rows => ({
      ...rows,
      [event.data?.[this.dataKey()]]: true,
    }));
    this.rowExpand.emit(event);
  }

  onRowCollapse(event: TableRowCollapseEvent): void {
    this.expandedRows.update(rows => {
      const next = { ...rows };
      if (event.data?.[this.dataKey()]) {
        delete next[event.data[this.dataKey()]];
      }
      return next;
    });
    this.rowCollapse.emit(event);
  }

  clearFilters(): void {
    const table = this.dt();
    if (table) {
      table.clear();
    }
    this.globalFilter.set('');
    this.columnFilters.set({});
    this.clearFiltersEvent.emit();
  }

  /** Toggle the per-column dynamic filter row */
  toggleFilters(): void {
    const next = !this.filtersVisible();
    this.filtersVisible.set(next);
    if (!next) {
      const table = this.dt();
      Object.keys(this.columnFilters()).forEach(field => table?.filter(null, field, 'contains'));
      this.columnFilters.set({});
      this.clearFiltersEvent.emit();
    }
  }

  /**
   * Resolves a column's rowspan for the given row:
   * undefined → render td normally, n > 1 → td with [attr.rowspan], 0 → skip td.
   */
  rowspanFor(col: TableColumn, rowIndex: number): number | undefined {
    if (!col.rowspanOf) return undefined;
    const span = col.rowspanOf(this.data(), rowIndex);
    if (span === 0) return 0;
    return span && span > 1 ? span : undefined;
  }

  rowClassFn(rowData: any): string {
    return this.rowStyleClassFn()?.(rowData) ?? '';
  }

  onColumnFilter(field: string, value: string): void {
    this.columnFilters.update(map => ({ ...map, [field]: value }));
    this.first.set(0);
    this.dt()?.filter(value || null, field, 'contains');
  }

  /** Explicit Search action - re-applies the current global filter from the top of the dataset */
  applySearch(): void {
    this.onGlobalFilter(this.globalFilter());
  }

  exportCsv(): void {
    this.dt()?.exportCSV({ selectionOnly: false });
  }

  resetPage(): void {
    this.first.set(0);
  }

  trackByField(_index: number, col: TableColumn): string {
    return col.field;
  }

  resyncData() {
    throw new Error('Method not implemented.');
  }

  @HostListener('window:resize')
  @HostListener('window:load')
  getTableHeightByPage(): string | undefined {
    if (!this.scrollable()) {
      return undefined;
    }
    const height = (this.scrollHeight() && this.scrollHeight() !== 'flex')
      ? this.scrollHeight()
      : '400px';
    const num = parseInt(height, 10);
    if (!isNaN(num)) {
      this.tableHeightEvent.emit(num);
    } else {
      this.tableHeightEvent.emit(400);
    }
    return height;
  }

  ngOnChanges() {
    this.getTableHeightByPage();
  }
  ngOnInit() {
    this.getTableHeightByPage();
  }

  ngAfterViewInit(): void {
    this.getTableHeightByPage();
        const table = this.dt();
    if (table && this.globalFilter()) {
      table.filterGlobal(this.globalFilter(), 'contains');
    }
  }
}
